/**
 * Adding a dependency, in either direction.
 *
 * A dialog rather than a popover: the popover this replaces was 320px wide,
 * showed six rows at a time, and gave each candidate only `#n title` — so
 * telling "Install windows, 2nd floor" from "Install windows, 3rd floor" meant
 * guessing. Each row now carries the status, the dates and who is on it, and
 * the list takes most of the screen height.
 *
 * The direction is still the first thing you choose, and the hint under it
 * follows the choice; it used to say "the issue this one waits on" on both.
 */
import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { AssigneeAvatar, StatusBadge } from "@/domains/pm/components/shared";
import { formatPanelDate, panelDateLocale } from "@/domains/pm/utils/panelDates";
import type { PMIssue } from "@/domains/pm/types";
import { sortCandidates } from "./sortCandidates";

export type DependencyDirection = "waiting" | "drives";

export const DependencyPickerDialog = observer(function DependencyPickerDialog({
  open,
  onOpenChange,
  candidates,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidates: PMIssue[];
  /** `waiting` — the picked issue comes first. `drives` — this issue does. */
  onAdd: (candidate: PMIssue, direction: DependencyDirection) => void;
}) {
  const { t, language } = useLanguage();
  const copy = t.pm.issues.dependencies;
  const [direction, setDirection] = useState<DependencyDirection>("waiting");
  useEffect(() => {
    if (open) setDirection("waiting");
  }, [open]);
  const sorted = useMemo(() => sortCandidates(candidates), [candidates]);
  const locale = panelDateLocale(language);

  const dates = (issue: PMIssue) =>
    issue.start_date || issue.due_date
      ? `${formatPanelDate(issue.start_date, locale)} → ${formatPanelDate(issue.due_date, locale)}`
      : copy.unscheduled;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 overflow-hidden p-0">
        <DialogHeader className="space-y-3 px-5 pb-3 pt-5">
          <DialogTitle className="text-base">{copy.addDependency}</DialogTitle>
          <div role="radiogroup" aria-label={copy.addDependency} className="grid grid-cols-2 gap-2">
            <DirectionTab
              active={direction === "waiting"}
              onClick={() => setDirection("waiting")}
              label={copy.addWaitingOn}
            />
            <DirectionTab
              active={direction === "drives"}
              onClick={() => setDirection("drives")}
              label={copy.addDrives}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {direction === "waiting" ? copy.addWaitingOnHint : copy.addDrivesHint}
          </p>
        </DialogHeader>
        <Command className="border-t">
          <CommandInput placeholder={copy.searchIssues} className="h-12 text-base sm:text-sm" />
          <CommandList className="max-h-[60vh]">
            <CommandEmpty>{copy.noIssuesFound}</CommandEmpty>
            <CommandGroup className="p-2">
              {sorted.map((candidate) => (
                <CommandItem
                  key={candidate.id}
                  // cmdk filters on this string, not on the rendered children —
                  // so the number has to be in it or "#12" finds nothing.
                  value={`#${candidate.issue_number} ${candidate.title}`}
                  onSelect={() => {
                    onOpenChange(false);
                    onAdd(candidate, direction);
                  }}
                  className="flex-col items-stretch gap-1 rounded-md border-l-2 border-transparent px-3 py-2.5 data-[selected='true']:border-primary data-[selected='true']:bg-accent"
                >
                  <div className="flex min-w-0 items-baseline gap-2">
                    <span className="shrink-0 text-xs font-semibold tabular-nums text-primary">
                      #{candidate.issue_number}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">
                      {candidate.title}
                    </span>
                  </div>
                  <div className="flex min-w-0 items-center gap-3 pl-0.5 text-xs text-muted-foreground">
                    <StatusBadge
                      status={candidate.status}
                      buildingProjectId={candidate.building_project_id}
                    />
                    <span className="truncate tabular-nums">{dates(candidate)}</span>
                    {(candidate.assignees?.length ?? 0) > 0 && (
                      <span className="ml-auto flex shrink-0 -space-x-1.5">
                        {candidate.assignees?.slice(0, 3).map((user) => (
                          <AssigneeAvatar
                            key={user.id}
                            user={user}
                            size={20}
                            className="ring-2 ring-background"
                          />
                        ))}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
});

function DirectionTab({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={cn(
        "rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background text-foreground hover:border-primary/50"
      )}
    >
      {label}
    </button>
  );
}
